import { useCallback, useMemo, useState } from 'react';

import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { filterUsableObjects, UsableObjectConfig } from 'dashboard/lib/workflows/objects';
import {
  WorkflowActionMeta,
  WorkflowError,
  WorkflowFieldObjectKey,
  WorkflowObjectKey,
} from 'dashboard/models/workflow';
import { WorkflowActionUnsetRelationV1 } from 'dashboard/models/workflow-version';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { ApiModel } from 'lib/interfaces/api';
import { Nullable } from 'lib/utils/types';

interface ParamsUnsetRelationV1Props {
  editable: boolean;
  errors: WorkflowError[];
  params: Nullable<WorkflowActionUnsetRelationV1['params']>;
  accountTypes: WorkflowActionMeta['account-types'];
  transactionTypes: WorkflowActionMeta['transaction-types'];
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: Nullable<WorkflowActionUnsetRelationV1['params']>) => void;
}

const DELIMITER = '?';

const ParamsUnsetRelationV1 = ({
  editable,
  errors,
  params,
  accountTypes,
  transactionTypes,
  usableObjects,
  updateActionParams,
}: ParamsUnsetRelationV1Props) => {
  const allTypes = useMemo(
    () => [...transactionTypes, ...accountTypes],
    [accountTypes, transactionTypes],
  );

  const filteredUsableObjects = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [WorkflowFieldObjectKey.Transaction, WorkflowFieldObjectKey.Account],
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);

  const extractObjectOptions = useCallback(
    ({ label, value, filters }: UsableObjectConfig) => {
      const objectType = allTypes.find((type) =>
        (filters?.['template-filters'] ?? []).some(({ id }) => id === type.token),
      );
      return {
        label: `${label}${objectType ? ` - ${objectType.name}` : ''}`,
        value,
      };
    },
    [allTypes],
  );

  const objectOptions: { label: string; value: string }[] = useMemo(
    () =>
      filteredUsableObjects
        .filter(({ workflowObjectKey: key }) =>
          [WorkflowFieldObjectKey.Transaction, WorkflowFieldObjectKey.Account].some(
            (objectKey) => objectKey === key,
          ),
        )
        .map(extractObjectOptions),
    [filteredUsableObjects, extractObjectOptions],
  );

  // `${typeId}?${relation name}`
  // TODO(kengo): persist selectedTypeToken on params instead of this internal state
  // Possibly this operation may pick unintentional schema if various transactionType have the same key of relation schema
  // We should persist transaction type's token on params to determine the exact type even backend doesn't require
  const [selectedSchema, setSelectedSchema] = useState<string | null>(() => {
    const selectedType = allTypes.find((type) =>
      type.relationSchemas.find(({ key }) => key === params['relation-schema-name']),
    );

    return selectedType
      ? [selectedType.token, params['relation-schema-name']].join(DELIMITER)
      : null;
  });

  const relationOptions = useMemo(() => {
    const extractOptions = (
      types: typeof allTypes,
    ): { label: string; value: string; hasMany: boolean }[] =>
      types.flatMap(({ name: typeName, relationSchemas, token }) =>
        relationSchemas.map(
          ({ cardinality, key, type: assignedType, token: assignedTypeToken }) => {
            const assignedObjectTypeName =
              allTypes.find((type) => type.token === assignedTypeToken)?.name ??
              (assignedType === ApiModel.AccountType ? 'Account' : 'Transaction');
            return {
              label: `${key} (${typeName} has ${cardinality} ${assignedObjectTypeName})`,
              value: [token, key].join(DELIMITER),
              hasMany: cardinality === 'many',
            };
          },
        ),
      );

    const target = filteredUsableObjects.find(({ value }) => value === params.object);
    if (target?.filters?.['template-filters']?.length ?? 0 > 0) {
      return extractOptions(
        allTypes.filter(({ token }) =>
          target?.filters?.['template-filters']?.some(({ id }) => id === token),
        ),
      );
    } else {
      const keyToTypes: Partial<Record<WorkflowObjectKey, typeof allTypes>> = {
        [WorkflowFieldObjectKey.Account]: accountTypes,
        [WorkflowFieldObjectKey.Transaction]: transactionTypes,
      };
      return extractOptions(
        target?.workflowObjectKey ? (keyToTypes[target.workflowObjectKey] ?? allTypes) : allTypes,
      );
    }
  }, [params, accountTypes, transactionTypes, allTypes, filteredUsableObjects]);

  const assignedObjectOptions = useMemo(() => {
    if (selectedSchema == null) return [];
    const typeToken = selectedSchema.split(DELIMITER)[0];
    const selectedRelationSchema = (
      allTypes.find(({ token }) => token === typeToken)?.relationSchemas ?? []
    ).find(({ key }) => key === params['relation-schema-name']);

    return filteredUsableObjects
      .filter(
        ({ workflowObjectKey: key }) =>
          selectedRelationSchema &&
          key ===
            {
              [ApiModel.AccountType]: WorkflowFieldObjectKey.Account,
              [ApiModel.TransactionType]: WorkflowFieldObjectKey.Transaction,
            }[selectedRelationSchema.type],
      )
      .map(extractObjectOptions);
  }, [selectedSchema, allTypes, params, filteredUsableObjects, extractObjectOptions]);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionUnsetRelationV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel htmlFor='params-unset-relation__select-object' labelText='Object' />
      <Select
        disabled={!editable}
        hasError={hasErrorFor('object')}
        id='params-unset-relation__select-object'
        placeholder='Object'
        value={params['object']}
        onValueChange={(newObject) => {
          updateActionParams({
            ...params,
            object: newObject,
          });
        }}
      >
        {objectOptions.map(({ label, value }) => (
          <Option key={value} value={value}>
            {label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('object')}
      {params['object'] && (
        <>
          <Spacer height={2} />
          <ParamLabel htmlFor='params-unset-relation__select-relation' labelText='Relation' />
          <Select
            disabled={!editable}
            hasError={hasErrorFor('relation-schema-name')}
            id='params-unset-relation__select-relation'
            value={selectedSchema}
            onValueChange={(newRelationSchema) => {
              setSelectedSchema(newRelationSchema);
              updateActionParams({
                ...params,
                'relation-schema-name': newRelationSchema.split(DELIMITER)[1],
              });
            }}
          >
            {relationOptions.map(({ label, value }) => (
              <Option key={value} value={value}>
                {label}
              </Option>
            ))}
          </Select>
          {renderErrorMessageFor('relation-schema-name')}
        </>
      )}
      {selectedSchema != null &&
        relationOptions.find(({ value }) => value === selectedSchema)?.hasMany && (
          <>
            <Spacer height={2} />
            <ParamLabel
              htmlFor='params-unset-relation__select-unset-object'
              labelText='Unset object'
            />
            <Select
              disabled={!editable}
              hasError={hasErrorFor('unset-object')}
              id='params-unset-relation__select-unset-object'
              value={params['unset-object']}
              placeholder='Not selected (Unset all assigned objects)'
              onValueChange={(newUnsetObject) => {
                updateActionParams({
                  ...params,
                  'unset-object': newUnsetObject === 'all' ? null : newUnsetObject,
                });
              }}
            >
              {params['unset-object'] && <Option value='all'>Deselect</Option>}
              {assignedObjectOptions.map(({ label, value }) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
            {renderErrorMessageFor('unset-object')}
          </>
        )}
    </>
  );
};

export default ParamsUnsetRelationV1;
